import React, { memo, useMemo } from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

const MAP_VARIANT_CARD_RENDER_SIZES = [
  '(max-width: 767px) 100vw',
  '(max-width: 1023px) 50vw',
  '(max-width: 1920px) 31.25vw',
  '20.833vw',
].join(', ');

const GRID_VARIANT_CARD_RENDER_SIZES = [
  '(max-width: 549px) 100vw',
  '(max-width: 767px) 50vw',
  '(max-width: 1439px) 26vw',
  '(max-width: 1920px) 18vw',
  '14vw',
].join(', ');

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    reviewSummaries,

    // Optional props for Inquiry modal behavior in ListingCard
    onSubmitInquiry,
    onManageDisableScrolling,
    sendInquiryError,
    sendInquiryInProgress,

    noResultsInfo,

  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  // Memoize pagination links to prevent unnecessary re-renders
  const paginationLinks = useMemo(() => {
    return pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;
  }, [pagination, pageName, listingTypeParam, search]);

  const cardRenderSizes = isMapVariant
    ? MAP_VARIANT_CARD_RENDER_SIZES
    : GRID_VARIANT_CARD_RENDER_SIZES;

  // Memoize listing cards rendering to prevent unnecessary re-renders
  const listingCards = useMemo(() => {
    return listings.map((l, index) => {
      const uuid = l?.id?.uuid;
      const summaryForListing = reviewSummaries && uuid ? reviewSummaries[uuid] : undefined;
      return (
        <ListingCard
          className={css.listingCard}
          key={uuid}
          listing={l}
          renderSizes={cardRenderSizes}
          setActiveListing={setActiveListing}
          reviewSummaries={summaryForListing}
          onSubmitInquiry={onSubmitInquiry}
          onManageDisableScrolling={onManageDisableScrolling}
          sendInquiryError={sendInquiryError}
          sendInquiryInProgress={sendInquiryInProgress}
          priorityImage={index < 4}
        />
      );
    });
  }, [listings, cardRenderSizes, setActiveListing, reviewSummaries, onSubmitInquiry, onManageDisableScrolling, sendInquiryError, sendInquiryInProgress]);

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listingCards}
        {props.children}
        {noResultsInfo && <div className={css.noResultsInfo}>{noResultsInfo}</div>}
      </div>
      {paginationLinks}
    </div>
  );
};

export default memo(SearchResultsPanel);
